import React, { useState } from 'react';
import { toggleHeartNote } from '../../services/notesService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as solidHeart } from '@fortawesome/free-solid-svg-icons';
import { faHeart as regularHeart } from '@fortawesome/free-regular-svg-icons';
import './HeartButton.css';

interface HeartButtonProps {
  noteId: string;
  initialHeartCount: number;
  initialIsHearted?: boolean;
  onHeartUpdated?: (heartCount: number, isHearted: boolean) => void;
  disabled?: boolean;
}

/**
 * Heart button component for toggling hearts on notes
 */
const HeartButton: React.FC<HeartButtonProps> = ({
  noteId,
  initialHeartCount,
  initialIsHearted = false,
  onHeartUpdated,
  disabled = false
}) => {
  const [isHearted, setIsHearted] = useState(initialIsHearted);
  const [heartCount, setHeartCount] = useState(initialHeartCount);
  const [isLoading, setIsLoading] = useState(false);

  const handleHeartToggle = async () => {
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    try {
      const result = await toggleHeartNote(noteId);
      // API returns: { noteId, hearted, newHeartCount }
      // Convert to expected format
      setIsHearted(result.hearted);
      setHeartCount(result.newHeartCount);
      
      // Call the callback if provided
      if (onHeartUpdated) {
        onHeartUpdated(result.newHeartCount, result.hearted);
      }
    } catch (error) {
      console.error('Error toggling heart:', error);
      // Optionally show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button 
      className={`heart-button ${isHearted ? 'hearted' : ''} ${disabled ? 'disabled' : ''}`}
      onClick={handleHeartToggle}
      disabled={disabled || isLoading}
      aria-label={isHearted ? 'Remove heart' : 'Add heart'}
      data-testid="heart-button"
    >
      <FontAwesomeIcon 
        icon={isHearted ? solidHeart : regularHeart} 
        className={`heart-icon ${isHearted ? 'hearted' : ''}`}
      />
      <span className="heart-count">{heartCount}</span>
    </button>
  );
};

export default HeartButton; 